import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogContentBase, DialogModule, DialogRef, KENDO_DIALOGS } from '@progress/kendo-angular-dialog';
import { EditorModule } from '@progress/kendo-angular-editor';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { LabelModule } from '@progress/kendo-angular-label';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import {
  Discipline,
  Evaluation,
  Organisation,
  OrganisationDiscipline,
  PlacementReviewType
} from '../../models/models.lib';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { OrganisationService } from '../../organisation/organisation.service';
import { EvaluationService } from '../../evaluation/evaluation.service';
import { OrganisationDisciplineService } from '../../organisation/organisation-discipline.service';

@Component({
  selector: 'casi-placement-review-add-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FontAwesomeModule,
    DialogModule,
    EditorModule,
    InputsModule,
    ButtonsModule,
    LabelModule,
    DropDownsModule,
    LayoutModule,
    AutoFocusDirective,
    KENDO_DIALOGS
  ],
  templateUrl: './placement-review-add-win.html',
  styleUrl: './placement-review-add-win.scss'
})
export class PlacementReviewAddWin implements OnInit {

  private _placementReview: PlacementReviewType | undefined;

  @Input() title: string = 'Title Not Set';


  @Input({ required: true }) set placementReview(val: PlacementReviewType) {
    this._placementReview = val;
    this.editForm.patchValue(this._placementReview);
  }
  get placementReview(): PlacementReviewType | undefined {
    return this._placementReview;
  };

  private _mode: FormModes = FormModes.NOTSET;
  @Input({ required: true }) set mode(val: FormModes) {
    this._mode = val;
  }
  get mode(): FormModes {
    return this._mode;
  };

  @Output() onSubmit: EventEmitter<FormGroup> = new EventEmitter();
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  editForm: FormGroup;
  disciplines: Discipline[] = [];
  organisations: Organisation[] = [];
  evaluations: Evaluation[] = [];
  formModes = FormModes;

  constructor(
    private fb: FormBuilder,
    private logger: LoggerService,
    private me: MeService,
    private disciplineService: OrganisationDisciplineService,
    private organisationService: OrganisationService,
    private evaluationService: EvaluationService
  ) {
    this.editForm = this.fb.group(
      {
        id: [null, []],
        organisation_id: [null, [Validators.required]],
        discipline_id: [null, [Validators.required]],
        evaluation_id: [null, [Validators.required]],
        unit_of_study: [null, [Validators.required]],
        client_contact_target_hours: [null, [Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
        individual_supervision_target_hours: [null, [Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
        group_supervision_target_hours: [null, [Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
        other_activities_target_hours: [null, [Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
        total_target_hours: [null, [Validators.min(0), Validators.pattern(/^\d+(\.\d{1,2})?$/)]]
      }
    );
  }

  //#region Form

  get f() { return this.editForm.controls; }
  get organisation_id() { return this.editForm.get('organisation_id'); }
  get discipline_id() { return this.editForm.get('discipline_id'); }
  get evaluation_id() { return this.editForm.get('evaluation_id'); }
  get unit_of_study() { return this.editForm.get('unit_of_study'); }
  get client_contact_target_hours() { return this.editForm.get('client_contact_target_hours'); }
  get individual_supervision_target_hours() { return this.editForm.get('individual_supervision_target_hours'); }
  get group_supervision_target_hours() { return this.editForm.get('group_supervision_target_hours'); }
  get other_activities_target_hours() { return this.editForm.get('other_activities_target_hours'); }
  get total_target_hours() { return this.editForm.get('total_target_hours'); }

  //#endregion

  //#region Ng handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    this.loadOrganisations();

    const orgId = this.editForm.get('organisation_id')?.value;
    if (orgId)
      this.loadDisciplines(orgId);

    const discId = this.editForm.get('discipline_id')?.value;
    if (discId)
      this.loadEvaluations(discId);

    if (this.mode === this.formModes.EDIT) {
      this.organisation_id?.disable();
      this.evaluation_id?.disable();
      this.discipline_id?.disable();
    } else {
      this.organisation_id?.enable();
      this.evaluation_id?.enable();
      this.discipline_id?.enable();
    }

    this.editForm.get('organisation_id')!.valueChanges.subscribe(id => {
      this.editForm.patchValue({ discipline_id: null });
      if (!id) {
        this.disciplines = [];
        return;
      }
      this.loadDisciplines(id);
    });

    this.editForm.get('discipline_id')!.valueChanges.subscribe(id => {
      this.editForm.patchValue({ evaluation_id: null });
      if (!id) {
        this.evaluations = [];
        return;
      }
      this.loadEvaluations(id);
    });
  }

  //#endregion

  //#region Public methods

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
  }

  public onSaveAction(): void {
    this.logger.debug(`onSaveAction...`, this.editForm.value);
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }
    this.onSubmit.emit(this.editForm);
  }

  //#endregion

  //#region Private methods

  private loadOrganisations(): void {
    if (this.me.email)
      this.organisationService.get().subscribe({
        // next: data => this.organisations = data ? data.filter((d: Organisation) => d.active) : [],
        next: data => this.organisations = data ? (this.mode == FormModes.NEW ? data.filter((d: Organisation) => d.active) : data) : [],
        error: err => console.error('Failed to load organisations', err)
      });
  }

  // private loadDisciplines(organisationId: number): void {
  //   this.logger.debug('loadDisciplines...', organisationId);
  //   this.disciplineService.get(organisationId).subscribe({
  //     next: data => this.disciplines = data ? (this.mode == FormModes.NEW ? data.filter((d: OrganisationDiscipline) => d.active) : data) : [],
  //     error: err => console.error('Failed to load disciplines', err)
  //   });
  // }

  private loadDisciplines(organisationId: number): void {
    this.logger.debug('loadDisciplines...', organisationId);
    this.disciplineService.fetchAll().subscribe({
      next: data => this.disciplines = data ? (this.mode == FormModes.NEW ? data.filter((d: OrganisationDiscipline) => d.active && d.organisation_id == organisationId) : data) : [],
      error: err => console.error('Failed to load disciplines', err)
    });
  }

  private loadEvaluations(disciplineId: number): void {
    this.logger.debug('loadEvaluations...', disciplineId);
    this.evaluationService.get().subscribe({
      // next: data => this.evaluations = data ? data.filter((d: Evaluation) => d.active && d.discipline_id == disciplineId) : [],
      next: data => this.evaluations = data ?
        (this.mode == FormModes.NEW ? data.filter((d: Evaluation) => d.active && d.discipline_id == disciplineId) : data.filter((d: Evaluation) => d.discipline_id == disciplineId))
        : [],
      error: err => console.error('Failed to load evaluations', err)
    });
  }

  //#endregion

}


